import type { ApiError } from '@services/http/client';

// Pull a human-readable message off an ApiError (or anything thrown), with a
// sensible fallback for the senders feature's inline error states.
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as ApiError).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

// Derive a friendly display name from an email address: turn the local part
// into Title Case ("billing.support@x" -> "Billing Support"). Falls back to the
// raw address when there's nothing useful to show.
export function displayFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email;
  const words = local
    .split(/[._+-]+/)
    .map((w) => w.trim())
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return words.length ? words.join(' ') : email;
}

// First letter for the avatar monogram, uppercased; '?' when empty.
export function monogram(value: string): string {
  const ch = value.trim().charAt(0);
  return ch ? ch.toUpperCase() : '?';
}

const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const WEEK = DAY * 7;

// Compact relative time ("2m ago", "3h ago", "5d ago") for the "Last seen"
// stat; falls back to an absolute date past ~4 weeks, '—' for missing input.
export function lastSeen(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '—';
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return '—';

  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  if (seconds < MINUTE) return 'just now';
  if (seconds < HOUR) return `${Math.floor(seconds / MINUTE)}m ago`;
  if (seconds < DAY) return `${Math.floor(seconds / HOUR)}h ago`;
  if (seconds < WEEK) return `${Math.floor(seconds / DAY)}d ago`;
  if (seconds < WEEK * 4) return `${Math.floor(seconds / WEEK)}w ago`;
  return then.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
